import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';
import { useAsinMonthlyResults } from '@/hooks/useAsinMonthlyResults';
import { formatYearOverYearValue, getRelevantMonths, calculateYearOverYearComparison, YearOverYearComparison } from '@/utils/yearOverYearCalculations';
import { MetricConfig } from '@/types/asinMonthlyResultsTypes';

/**
 * Format comparison values for display under the main YoY data
 * Handles different formatting requirements for different metric types
 */
const formatComparisonValue = (value: number | null, formatter: MetricConfig['formatter'], metricLabel: string): string => {
  if (value === null || value === undefined) {
    return '-';
  }

  switch (formatter) {
    case 'currency':
      // Format as currency with no decimals and commas
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(Math.round(value));
      
    case 'currencyWithCents':
      // Format as currency with cents (for PPC Cost Per Click) - don't round
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
      
    case 'percentage':
      // Format as percentage with one decimal place - multiply by 100 since values are decimals
      return `${(value * 100).toFixed(1)}%`;
      
    case 'wholeNumber':
    default:
      // Format as whole number with commas
      return Math.round(value).toLocaleString('en-US');
  }
};

/**
 * Get the formatter type for a metric based on its label
 */
const getFormatterForMetric = (metricLabel: string): MetricConfig['formatter'] => {
  const lowerLabel = metricLabel.toLowerCase();
  
  // PPC Cost Per Click should show cents
  if (lowerLabel.includes('cost per click')) {
    return 'currencyWithCents';
  }
  
  // Percentage metrics (with one decimal place) - check these BEFORE currency
  if (lowerLabel.includes('conversion rate') || lowerLabel.includes('margin') ||
      lowerLabel.includes('acos') || lowerLabel.includes('tacos') ||
      lowerLabel.includes('click through rate')) {
    return 'percentage';
  }
  
  // Other currency metrics should be whole dollars (but exclude margins which are percentages)
  if ((lowerLabel.includes('revenue') || lowerLabel.includes('profit') ||
       lowerLabel.includes('sales') || lowerLabel.includes('spend')) &&
      !lowerLabel.includes('margin')) {
    return 'currency';
  }
  
  return 'wholeNumber';
};

/**
 * Get background color class based on percentage change value and formatted display
 * Red for negative, light yellow for zero, green for positive
 */
const getBackgroundColorForChange = (percentageChange: number | null, formattedDisplay: string): string => {
  // Check if the display shows "/ 0%" which indicates no change (when previous value was 0)
  if (formattedDisplay.includes('/ 0%')) {
    return 'bg-yellow-50 dark:bg-yellow-900/20'; // Light yellow for zero change
  }
  
  if (percentageChange === null || percentageChange === undefined) {
    return ''; // No special color for null/undefined values
  }
  
  if (percentageChange < 0) {
    return 'bg-red-100 dark:bg-red-900/30'; // Red for negative
  } else if (percentageChange === 0) {
    return 'bg-yellow-50 dark:bg-yellow-900/20'; // Light yellow for zero
  } else {
    return 'bg-green-100 dark:bg-green-900/30'; // Green for positive
  }
};

export const YearOverYearDashboard: React.FC = () => {
  const { selectedCompany, selectedProduct } = usePMT3Selection();
  const { data: monthlyData, isLoading, error } = useAsinMonthlyResults(
    selectedCompany,
    selectedProduct?.id
  );

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Year Over Year Change
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Loading year over year data...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Year Over Year Change
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-red-500">Error loading data: {error.message}</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!selectedProduct && !isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Year Over Year Change
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">Select a product to view year over year data.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!monthlyData && !isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Year Over Year Change
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">No monthly results data available for this ASIN.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get the relevant months (current + 3 previous)
  const relevantMonths = getRelevantMonths();

  // Calculate year over year comparisons for the selected ASIN
  const yoyComparisons = monthlyData ? calculateYearOverYearComparison(monthlyData, relevantMonths) : [];

  // Get unique metrics from the comparisons
  const metrics = Array.from(new Set(yoyComparisons.map(comp => comp.metric)));

  if (metrics.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold text-gray-900">
            Year Over Year Change
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-gray-500">No metrics available for year over year comparison.</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="p-4">
        <CardTitle className="text-2xl font-bold">Year Over Year Change</CardTitle>
        {selectedProduct && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Showing year-over-year comparison for {selectedProduct.id}
          </p>
        )}
      </CardHeader>
      <CardContent className="p-4">
        {/* True spreadsheet-like table with unified rows */}
        <div className="w-full border rounded-lg overflow-hidden bg-white dark:bg-gray-900">
          <div className="h-[600px] overflow-auto">
            <table className="w-full border-collapse">
              {/* Header row */}
              <thead className="sticky top-0 z-20 bg-gray-100 dark:bg-gray-700">
                <tr>
                  <th className="sticky left-0 z-30 bg-gray-100 dark:bg-gray-700 px-4 py-3 text-left font-bold text-gray-900 dark:text-gray-100 border-r border-b min-w-[200px]">
                    Metric
                  </th>
                  {relevantMonths.map((month: string) => (
                    <th
                      key={month}
                      className="px-3 py-3 text-center font-bold text-gray-900 dark:text-gray-100 border-r border-b min-w-[120px] whitespace-nowrap"
                    >
                      {month}
                    </th>
                  ))}
                </tr>
              </thead>
              
              {/* Data rows */}
              <tbody>
                {metrics.map((metric, rowIndex) => {
                  const metricComparisons = yoyComparisons.filter(comp => comp.metric === metric);
                  
                  return (
                    <tr key={metric} className={`hover:bg-gray-100 dark:hover:bg-gray-600 ${rowIndex % 2 === 0 ? 'bg-white dark:bg-gray-900' : 'bg-gray-50 dark:bg-gray-800'}`}>
                      {/* Metric name cell - sticky */}
                      <td className="sticky left-0 z-10 bg-inherit px-4 py-3 font-semibold text-gray-900 dark:text-gray-100 border-r border-b min-w-[200px]">
                        {metric}
                      </td>
                      
                      {/* Month data cells */}
                      {relevantMonths.map((month: string) => {
                        const comparison = metricComparisons.find(comp => comp.month === month);
                        const backgroundColorClass = comparison ? getBackgroundColorForChange(comparison.percentageChange, comparison.formattedDisplay) : '';
                        
                        return (
                          <td
                            key={`${metric}-${month}`}
                            className={`px-3 py-3 text-center border-r border-b min-w-[120px] whitespace-nowrap ${backgroundColorClass}`}
                          >
                            {comparison ? (
                              <div className="space-y-1">
                                <div className="font-medium text-sm">
                                  {formatYearOverYearValue(comparison)}
                                </div>
                                {comparison.currentValue !== null && comparison.previousValue !== null && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    {formatComparisonValue(comparison.currentValue, getFormatterForMetric(metric), metric)} vs {formatComparisonValue(comparison.previousValue, getFormatterForMetric(metric), metric)}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer information */}
        {metrics.length > 0 && (
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            Showing {metrics.length} metrics across {relevantMonths.length} months • Last updated: {new Date().toLocaleString()}
          </div>
        )}
      </CardContent>
    </Card>
  );
};